import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeType = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);

  // Signal to track current theme
  currentTheme = signal<ThemeType>('light');

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initTheme();
    }
  }

  private initTheme(): void {
    const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Set initial theme
    const initialTheme = darkMediaQuery.matches ? 'dark' : 'light';
    this.setTheme(initialTheme);
    // Force immediate update to avoid flash (waiting for effect)
    this.updateThemeStyles(initialTheme);

    // Watch for system theme changes
    darkMediaQuery.addEventListener('change', (e) => {
      this.setTheme(e.matches ? 'dark' : 'light');
    });

    // Effect to update styles when signal changes
    effect(() => {
      this.updateThemeStyles(this.currentTheme());
    });

    // Observe head for new links (important for Angular dev server injection)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === 1 &&
            (node as Element).tagName === 'LINK' &&
            (node as Element).getAttribute('rel') === 'stylesheet'
          ) {
            const link = node as HTMLLinkElement;
            const theme = this.currentTheme();
            if (link.href.includes('theme-light')) {
              link.disabled = theme === 'dark';
            }
            if (link.href.includes('theme-dark')) {
              link.disabled = theme === 'light';
            }
          }
        });
      });
    });
    observer.observe(document.head, { childList: true, subtree: true });
  }

  setTheme(theme: ThemeType): void {
    this.currentTheme.set(theme);
  }

  toggleTheme(): void {
    this.currentTheme.update((prev) => (prev === 'light' ? 'dark' : 'light'));
  }

  private updateThemeStyles(theme: ThemeType): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const themeHref = `${theme === 'dark' ? 'theme-dark' : 'theme-light'}.css`;
    let themeLink = document.getElementById('app-theme') as HTMLLinkElement;

    if (themeLink) {
      themeLink.href = themeHref;
    } else {
      // Create new link tag
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.id = 'app-theme';
      link.href = themeHref;
      document.head.appendChild(link);
    }

    // Support for dev server or other injected links
    const links = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]'),
    ) as HTMLLinkElement[];

    links.forEach((link) => {
      if (link.id === 'app-theme') return;
      const href = link.getAttribute('href') || '';
      if (href.includes('theme-light')) {
        link.disabled = theme === 'dark';
      }
      if (href.includes('theme-dark')) {
        link.disabled = theme === 'light';
      }
    });

    // Also update body class
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }
}
